import * as Immutable from 'immutable';
import moment from 'moment';
import { Action, handleActions } from 'redux-actions';

import {
  COMPLETED_STATUS,
  EDITED_STATUS,
  ERROR_STATUS,
  NEW_STATUS,
  SIMULATING_STATUS,
  UNREAD_STATUS,
  WARNING_STATUS,
} from '../../../shared/constants';
import { chartMapping } from '../../../shared/data/chartSeriesChange';
import { getChartTable } from '../../../shared/helpers';
import {
  formatPerformanceData,
  formatPerformanceStats,
  getAlphaSummary,
  updateAlphasSummary,
} from '../../AlphasReducer/AlphasReducerHelpers';

import * as Sentry from '@sentry/react';
import { AlphasActionType, AlphasStoreElement, CorrelationSelfType } from '../../AlphasReducer/AlphasReducer.types';
import { getStartDates } from '../../GenericSelectors';
import { walkthroughWithData } from './chartWalkthrough';
import { CorrelationFailureDataActionType, DataActionType, DataStoreList } from './DataReducer.types';

const initialState = Immutable.List();

const alphaPropertiesInitialState = Immutable.fromJS({
  color: null,
  name: null,
  tags: [],
  category: null,
  regular: {
    description: '',
  },
});

const superAlphaPropertiesInitialState = Immutable.fromJS({
  color: null,
  name: null,
  tags: [],
  category: null,
  selection: {
    description: '',
  },
  combo: {
    description: '',
  },
});

const walkthroughWithoutData = {
  fetchingData: false,
  alphaDetail: null,
  chartType: 'pnl',
};

const simulationStatusPriority: any = {
  error: 6,
  warning: 5,
  unread: 4,
  edited: 3,
  simulating: 2,
  new: 1,
  completed: 0,
};

const getParentStatus = (childTabs: Immutable.List<any> | undefined, defaultStatusValue: string = NEW_STATUS, skipChild?: number) => {
  let parentStatus: any = defaultStatusValue;
  if (childTabs == null || typeof childTabs.forEach !== 'function') {
    return parentStatus;
  }
  childTabs.forEach((childTab: any, index: number) => {
    const childStatus: any = childTab.get('status');
    if (
      !(typeof skipChild !== 'undefined' && skipChild === index) &&
      simulationStatusPriority[childStatus] > simulationStatusPriority[parentStatus]
    ) {
      parentStatus = childTab.get('status');
    }
  });

  return parentStatus;
};

const setNewStatus = (state: DataStoreList, tabIndex: number, indexChild: number, nextStatus: string) => {
  const childTabs = state.getIn([tabIndex, 'children']);
  if ((childTabs && indexChild) || indexChild === 0) {
    return (
      state
        .setIn([tabIndex, 'children', indexChild, 'status'], nextStatus)
        // @ts-ignore
        .setIn([tabIndex, 'status'], getParentStatus(childTabs, nextStatus, indexChild))
    );
  } else {
    // @ts-ignore
    if (childTabs && childTabs.size > 0) {
      // @ts-ignore
      return state.setIn([tabIndex, 'status'], getParentStatus(childTabs, nextStatus));
    } else {
      return state.setIn([tabIndex, 'status'], nextStatus);
    }
  }
};

function getChildTabStartDates(state: DataStoreList, tabIndex: number, indexChild: number) {
  const osStartDate = state.getIn([tabIndex, 'children', indexChild, 'alphaDetail', 'os', 'startDate']);
  const testStartDate = state.getIn([tabIndex, 'children', indexChild, 'alphaDetail', 'test', 'startDate']);
  const validationStartDate = state.getIn([tabIndex, 'children', indexChild, 'alphaDetail', 'validation', 'startDate']);
  const trainStartDate = state.getIn([tabIndex, 'children', indexChild, 'alphaDetail', 'train', 'startDate']);
  return { osStartDate, testStartDate, validationStartDate, trainStartDate };
}

function getTabStartDates(state: DataStoreList, tabIndex: number) {
  const osStartDate = state.getIn([tabIndex, 'alphaDetail', 'os', 'startDate']);
  const testStartDate = state.getIn([tabIndex, 'alphaDetail', 'test', 'startDate']);
  const validationStartDate = state.getIn([tabIndex, 'alphaDetail', 'validation', 'startDate']);
  const trainStartDate = state.getIn([tabIndex, 'alphaDetail', 'train', 'startDate']);
  return { osStartDate, testStartDate, validationStartDate, trainStartDate };
}

const createRequestSimulationCorrelation = (fetchingField: string, correlationField: string) => {
  return (state: DataStoreList, action: DataActionType) => {
    const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
    const newState = state;
    if (tabIndex >= 0) {
      const childId = action.payload.childId;
      const children = state.getIn([tabIndex, 'children']);
      // @ts-ignore
      const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
      const hasAlphaDetailChildren = newState.getIn([tabIndex, 'children', indexChild, 'alphaDetail']);
      const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
      if (indexChild !== null && indexChild !== undefined && indexChild >= 0 && hasAlphaDetailChildren) {
        return newState
          .setIn([tabIndex, 'children', indexChild, 'alphaDetail', fetchingField], true)
          .setIn([tabIndex, 'children', indexChild, 'alphaDetail', correlationField], null);
      } else if (!children && hasAlphaDetail) {
        return newState.setIn([tabIndex, 'alphaDetail', fetchingField], true).setIn([tabIndex, 'alphaDetail', correlationField], null);
      } else {
        return state;
      }
    } else {
      return state;
    }
  };
};

const createResponseSimulationCorrelationFailure = (fetchingField: string) => {
  return (state: DataStoreList, action: CorrelationFailureDataActionType) => {
    const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
    const newState = state;
    if (tabIndex >= 0) {
      const childId = action.payload.childId;
      const children = state.getIn([tabIndex, 'children']);
      // @ts-ignore
      const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
      const simulationStatePath = [tabIndex, 'children', indexChild, 'alphaDetail'];
      const hasAlphaDetailChildren = newState.getIn(simulationStatePath);
      const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
      if (indexChild !== null && indexChild !== undefined && indexChild >= 0 && hasAlphaDetailChildren) {
        return newState
          .setIn([...simulationStatePath, 'correlationStatusCode'], action.payload.statusCode)
          .setIn([...simulationStatePath, fetchingField], false);
      } else if (!children && hasAlphaDetail) {
        const alphaStatePath = [tabIndex, 'alphaDetail'];
        return newState
          .setIn([...alphaStatePath, 'correlationStatusCode'], action.payload.statusCode)
          .setIn([...alphaStatePath, fetchingField], false);
      } else {
        return state;
      }
    } else {
      return state;
    }
  };
};

const createResponseSimulationCorrelationSuccess = (valueField: string, lastRunField: string, fetchingField: string) => {
  return (state: DataStoreList, action: Action<{ idParent: string; childId: string; data: CorrelationSelfType }>) => {
    const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
    const newState = state;
    if (tabIndex >= 0) {
      const childId = action.payload.childId;
      const children = state.getIn([tabIndex, 'children']);
      // @ts-ignore
      const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
      const hasAlphaDetailChildren = newState.getIn([tabIndex, 'children', indexChild, 'alphaDetail']);
      const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
      if (indexChild !== null && indexChild !== undefined && indexChild >= 0 && hasAlphaDetailChildren) {
        return newState
          .setIn([tabIndex, 'children', indexChild, 'alphaDetail', fetchingField], false)
          .setIn([tabIndex, 'children', indexChild, 'alphaDetail', valueField], Immutable.fromJS(action.payload.data))
          .setIn([tabIndex, 'children', indexChild, 'alphaDetail', lastRunField], moment().utc().format());
      } else if (!children && hasAlphaDetail) {
        return newState
          .setIn([tabIndex, 'alphaDetail', fetchingField], false)
          .setIn([tabIndex, 'alphaDetail', valueField], Immutable.fromJS(action.payload.data))
          .setIn([tabIndex, 'alphaDetail', lastRunField], moment().utc().format());
      } else {
        return state;
      }
    } else {
      return state;
    }
  };
};

export const DataReducer = handleActions<any>(
  {
    REQUEST_DATA_SIMULATE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      if (tabIndex >= 0) {
        return state.setIn([tabIndex, 'fetchingData'], true);
      } else {
        return state;
      }
    },

    REQUEST_SIMULATION_POST: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const children = state.getIn([tabIndex, 'children']);
      if (tabIndex >= 0) {
        if (children) {
          return state
            .updateIn([tabIndex, 'children'], (val: any) =>
              val.map((el: any) =>
                el.merge(
                  Immutable.fromJS({
                    alphaDetail: null,
                    fetchingData: false,
                    chartType: 'pnl',
                    chartData: null,
                    allActive: false,
                    hideActive: false,
                    status: SIMULATING_STATUS,
                    statusAfterSimulation: null,
                    isTutorialModeActive: action.payload.isTutorialModeActive,
                    tutorialStep: action.payload.tutorialStep,
                  })
                )
              )
            )
            .setIn([tabIndex, 'alphaSelections'], null)
            .setIn([tabIndex, 'status'], SIMULATING_STATUS)
            .setIn([tabIndex, 'statusAfterSimulation'], null);
        } else {
          return state.update(tabIndex, (val: any) =>
            val.merge(
              Immutable.fromJS({
                alphaDetail: null,
                fetchingData: false,
                chartType: 'pnl',
                chartData: null,
                allActive: false,
                hideActive: false,
                alphaSelections: null,
                selectionCodeSettings: null,
                alphasFromSelectionCode: null,
                status: SIMULATING_STATUS,
                statusAfterSimulation: null,
                isTutorialModeActive: action.payload.isTutorialModeActive,
                tutorialStep: action.payload.tutorialStep,
              })
            )
          );
        }
      } else {
        return state;
      }
    },

    RESPONSE_DATA_SIMULATE_SUCCESS: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      let newState = state;
      const alpha = action.payload.alpha;
      const immutableData = Immutable.fromJS({
        ...alpha,
        performanceAlphaFetching: false,
        performanceAlphaSummary: getAlphaSummary(),
        performanceAlphaLastDate: null,
      });
      const childId = action.payload.childId;
      const children = state.getIn([tabIndex, 'children']);
      // @ts-ignore
      const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
      if (tabIndex >= 0) {
        newState = newState.setIn([tabIndex, 'fetchingData'], false);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          const hasUpdatedProperties = state.getIn([tabIndex, 'children', indexChild, 'alphaProperties', 'hasUpdatedProperties']);
          const alphaProperties = hasUpdatedProperties
            ? state.getIn([tabIndex, 'children', indexChild, 'alphaProperties'])
            : Immutable.fromJS({
                color: alpha.color && alpha.color !== 'NONE' && alpha.color.trim().length !== 0 ? alpha.color : null,
                name: alpha.name !== '' ? alpha.name : null,
                tags: alpha.tags,
                category: alpha.category !== '' ? alpha.category : null,
                regular: alpha.regular,
                selection: alpha.selection,
                combo: alpha.combo,
              });
          let hasUpdatedPropertiesInAlpha = hasUpdatedProperties;
          // @ts-ignore
          alphaProperties.map((property: any) => {
            if (property !== null && property !== false) {
              hasUpdatedPropertiesInAlpha = true;
            }
          });
          let nextChildStatus = state.getIn([tabIndex, 'children', indexChild, 'status']);
          nextChildStatus = nextChildStatus !== WARNING_STATUS ? UNREAD_STATUS : nextChildStatus;

          return newState
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail'], immutableData)
            .setIn([tabIndex, 'children', indexChild, 'summaryOSPROD'], Immutable.fromJS(getChartTable(immutableData)))
            .setIn([tabIndex, 'children', indexChild, 'alphaProperties'], alphaProperties)
            .setIn([tabIndex, 'children', indexChild, 'alphaProperties', 'hasUpdatedProperties'], hasUpdatedPropertiesInAlpha)
            .setIn([tabIndex, 'children', indexChild, 'status'], nextChildStatus)
            .setIn([tabIndex, 'children', indexChild, 'statusAfterSimulation'], nextChildStatus)
            .setIn([tabIndex, 'status'], nextChildStatus)
            .setIn([tabIndex, 'statusAfterSimulation'], nextChildStatus);
        } else if (!children) {
          const hasUpdatedProperties = state.getIn([tabIndex, 'alphaProperties', 'hasUpdatedProperties']);
          const alphaProperties = hasUpdatedProperties
            ? state.getIn([tabIndex, 'alphaProperties'])
            : Immutable.fromJS({
                color: alpha.color && alpha.color !== 'NONE' && alpha.color.trim().length !== 0 ? alpha.color : null,
                name: alpha.name !== '' ? alpha.name : null,
                tags: alpha.tags,
                category: alpha.category !== '' ? alpha.category : null,
                regular: alpha.regular,
                selection: alpha.selection,
                combo: alpha.combo,
              });
          let hasUpdatedPropertiesInAlpha = hasUpdatedProperties;
          // @ts-ignore
          alphaProperties.map((property: any) => {
            if (property !== null && property !== false) {
              hasUpdatedPropertiesInAlpha = true;
            }
          });
          let nextStatus = state.getIn([tabIndex, 'status']);
          nextStatus = nextStatus !== WARNING_STATUS ? UNREAD_STATUS : nextStatus;

          return newState
            .setIn([tabIndex, 'alphaDetail'], immutableData)
            .setIn([tabIndex, 'summaryOSPROD'], Immutable.fromJS(getChartTable(immutableData)))
            .setIn([tabIndex, 'alphaProperties'], alphaProperties)
            .setIn([tabIndex, 'alphaProperties', 'hasUpdatedProperties'], hasUpdatedPropertiesInAlpha)
            .setIn([tabIndex, 'status'], nextStatus)
            .setIn([tabIndex, 'statusAfterSimulation'], nextStatus);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_DATA_SIMULATE_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      if (tabIndex >= 0) {
        return state.setIn([tabIndex, 'fetchingData'], false);
      } else {
        return state;
      }
    },
    CREATE_NEW_TAB: (state: DataStoreList, action: DataActionType) =>
      state.push(
        // @ts-ignore
        Immutable.fromJS({
          fetchingData: false,
          alphaDetail: null,
          chartType: 'pnl',
          id: action.payload.idParent,
          summaryIsOpen: true,
          alphaProperties: alphaPropertiesInitialState,
          status: NEW_STATUS,
          statusAfterSimulation: null,
        })
      ),
    CREATE_NEW_SUPER_ALPHA_TAB: (state: DataStoreList, action: DataActionType) =>
      state.push(
        // @ts-ignore
        Immutable.fromJS({
          fetchingData: false,
          alphaDetail: null,
          chartType: 'pnl',
          id: action.payload.idParent,
          summaryIsOpen: true,
          superAlpha: true,
          alphaProperties: superAlphaPropertiesInitialState,
          status: NEW_STATUS,
          statusAfterSimulation: null,
        })
      ),
    CREATE_NEW_MULTI_TAB: (state: DataStoreList, action: DataActionType) =>
      state.push(
        // @ts-ignore
        Immutable.fromJS({
          children: Immutable.List([
            Immutable.fromJS({
              alphaDetail: null,
              chartType: 'pnl',
              id: action.payload.idChilds[0],
              alphaProperties: alphaPropertiesInitialState,
              status: NEW_STATUS,
              statusAfterSimulation: null,
            }),
            Immutable.fromJS({
              alphaDetail: null,
              chartType: 'pnl',
              id: action.payload.idChilds[1],
              alphaProperties: alphaPropertiesInitialState,
              status: NEW_STATUS,
              statusAfterSimulation: null,
            }),
          ]),
          id: action.payload.idParent,
          fetchingData: false,
          status: NEW_STATUS,
          statusAfterSimulation: null,
        })
      ),
    CREATE_NEW_CHILD_TAB: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = action.payload.tabIndex;

      return state.setIn(
        [tabIndex, 'children'],
        // @ts-ignore
        state.getIn([tabIndex, 'children']).push(
          Immutable.fromJS({
            alphaDetail: null,
            chartType: 'pnl',
            id: action.payload.idChilds[0],
            alphaProperties: alphaPropertiesInitialState,
            status: NEW_STATUS,
            statusAfterSimulation: null,
          })
        )
      );
    },
    CLOSE_TAB: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = action.payload.tabIndex;

      return state.remove(tabIndex);
    },

    CLOSE_CHILD_TAB: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = action.payload.tabIndex;
      const childTabIndex = action.payload.indexChild;

      return state.removeIn([tabIndex, 'children', childTabIndex]);
    },
    CLONE_ALPHA_FROM_CONTENT: (state: DataStoreList, action: DataActionType) => {
      const data: any = action.payload.data ? Immutable.fromJS(action.payload.data) : null;
      const alphaProperties = Immutable.fromJS({
        color:
          data && data.get('color') && data.get('color') !== 'NONE' && data.get('color').trim().length !== 0 ? data.get('color') : null,
        name: data && data.get('name') !== '' ? data.get('name') : null,
        tags: data && data.get('tags'),
        category: data && data.get('category') !== '' ? data.get('category') : null,
        regular: data && data.get('regular'),
        selection: data && data.get('selection'),
        combo: data && data.get('combo'),
      });

      return state.push(
        // @ts-ignore
        Immutable.fromJS({
          fetchingData: false,
          alphaDetail: null,
          chartType: 'pnl',
          id: action.payload.idParent,
          summaryIsOpen: true,
          alphaProperties,
          superAlpha: data && data.get('type') === 'SUPER' ? true : false,
          status: NEW_STATUS,
          statusAfterSimulation: null,
        })
      );
    },
    CLONE_ALPHA: (state: DataStoreList, action: DataActionType) => {
      const tab = state.get(action.payload.tabIndex);
      const childrenActiveTab = state.getIn([action.payload.tabIndex, 'children']);
      const isMulti = action.payload.isMulti;
      if (isMulti && tab) {
        return state
          .push(tab)
          .setIn([-1, 'fetchingData'], false)
          .setIn([-1, 'id'], action.payload.idParent)
          .setIn([-1, 'status'], NEW_STATUS)
          .setIn([-1, 'statusAfterSimulation'], null)
          .updateIn([-1, 'children'], (children: any) =>
            children.map((child: any, key: number) =>
              Immutable.fromJS({
                alphaDetail: null,
                chartType: 'pnl',
                id: action.payload.idChilds[key],
                alphaProperties: child && child.get('alphaProperties'),
                status: NEW_STATUS,
                statusAfterSimulation: null,
              })
            )
          );
      } else if (childrenActiveTab && action.payload.tabIndex && tab) {
        return state.setIn(
          [action.payload.tabIndex, 'children'],
          // @ts-ignore
          tab.getIn(['children']).push(
            Immutable.fromJS({
              alphaDetail: null,
              chartType: 'pnl',
              id: action.payload.idParent,
              alphaProperties: alphaPropertiesInitialState,
              status: NEW_STATUS,
              statusAfterSimulation: null,
            })
          )
        );
      } else if (!childrenActiveTab) {
        return state.push(
          // @ts-ignore
          Immutable.fromJS({
            fetchingData: false,
            alphaDetail: null,
            chartType: 'pnl',
            id: action.payload.idParent,
            alphaProperties: tab && tab.get('alphaProperties'),
            superAlpha: tab && tab.get('superAlpha'),
            status: NEW_STATUS,
            statusAfterSimulation: null,
          })
        );
      }
    },
    CHANGE_TO_SIMULATION: (state: DataStoreList, action: DataActionType) =>
      state.set(
        action.payload.tabIndex,
        // @ts-ignore
        Immutable.fromJS({
          fetchingData: false,
          alphaDetail: null,
          chartType: 'pnl',
          id: state.getIn([action.payload.tabIndex, 'id']),
          summaryIsOpen: true,
          alphaProperties: alphaPropertiesInitialState,
          status: NEW_STATUS,
          statusAfterSimulation: null,
        })
      ),
    CHANGE_TO_MULTI_TAB: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = action.payload.tabIndex;

      return state.set(
        tabIndex,
        // @ts-ignore
        Immutable.fromJS({
          children: Immutable.List([
            Immutable.fromJS({
              alphaDetail: null,
              chartType: 'pnl',
              id: action.payload.idChilds[0],
              alphaProperties: alphaPropertiesInitialState,
              status: NEW_STATUS,
              statusAfterSimulation: null,
            }),
            Immutable.fromJS({
              alphaDetail: null,
              chartType: 'pnl',
              id: action.payload.idChilds[1],
              alphaProperties: alphaPropertiesInitialState,
              status: NEW_STATUS,
              statusAfterSimulation: null,
            }),
          ]),
          fetchingData: false,
          id: action.payload.idParent,
          status: NEW_STATUS,
          statusAfterSimulation: null,
        })
      );
    },
    RESPONSE_SIMULATION_CHECK: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      let newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        const hasAlphaDetailChildren = newState.getIn([tabIndex, 'children', indexChild, 'alphaDetail']);
        const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          if (hasAlphaDetailChildren) {
            newState = newState.setIn(
              [tabIndex, 'children', indexChild, 'alphaDetail', 'is', 'checks'],
              Immutable.fromJS(action.payload.alpha)
            );
          }

          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingCheck'], false);
        } else if (!children) {
          if (hasAlphaDetail) {
            newState = newState.setIn([tabIndex, 'alphaDetail', 'is', 'checks'], Immutable.fromJS(action.payload.alpha));
          }

          return newState.setIn([tabIndex, 'fetchingCheck'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_SIMULATION_CHECK: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingCheck'], true);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingCheck'], true);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_SIMULATION_CHECK_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingCheck'], false);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingCheck'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_RECORDS_SET_CHART_SIMULATION_SUCCESS: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          const { osStartDate, testStartDate, validationStartDate, trainStartDate } = getChildTabStartDates(state, tabIndex, indexChild);
          const chartType = state.getIn([tabIndex, 'children', indexChild, 'chartType']);
          const schema = action.payload.data.schema;
          const records = action.payload.data.records;
          // @ts-ignore
          const getRecordSeries = chartMapping[chartType];
          const series = getRecordSeries({
            chartDataSchema: schema,
            chartDataRecords: records,
            osStartDate,
            testStartDate,
            validationStartDate,
            trainStartDate,
            recordSet: chartType,
          });
          let nextChildStatus = state.getIn([tabIndex, 'children', indexChild, 'status']);
          nextChildStatus = nextChildStatus !== WARNING_STATUS ? UNREAD_STATUS : nextChildStatus;

          return newState
            .setIn([tabIndex, 'children', indexChild, 'fetchingRecordSetsChart'], false)
            .setIn([tabIndex, 'children', indexChild, 'chartData'], Immutable.fromJS(action.payload.data))
            .setIn([tabIndex, 'children', indexChild, 'chartData', 'records'], Immutable.fromJS(series))
            .setIn([tabIndex, 'children', indexChild, 'status'], nextChildStatus)
            .setIn([tabIndex, 'children', indexChild, 'statusAfterSimulation'], nextChildStatus)
            .setIn([tabIndex, 'status'], nextChildStatus)
            .setIn([tabIndex, 'statusAfterSimulation'], nextChildStatus);
        } else if (!children) {
          const { osStartDate, testStartDate, validationStartDate, trainStartDate } = getTabStartDates(state, tabIndex);
          const chartType = state.getIn([tabIndex, 'chartType']);
          const schema = action.payload.data.schema;
          const records = action.payload.data.records;
          // @ts-ignore
          const getRecordSeries = chartMapping[chartType];
          const series = getRecordSeries({
            chartDataSchema: schema,
            chartDataRecords: records,
            osStartDate,
            testStartDate,
            validationStartDate,
            trainStartDate,
            recordSet: chartType,
            isSuperAlpha: state.getIn([tabIndex, 'superAlpha']),
          });
          let nextStatus = state.getIn([tabIndex, 'status']);
          nextStatus = nextStatus !== WARNING_STATUS ? UNREAD_STATUS : nextStatus;

          return newState
            .setIn([tabIndex, 'fetchingRecordSetsChart'], false)
            .setIn([tabIndex, 'chartData'], Immutable.fromJS(action.payload.data))
            .setIn([tabIndex, 'chartData', 'records'], Immutable.fromJS(series))
            .setIn([tabIndex, 'status'], nextStatus)
            .setIn([tabIndex, 'statusAfterSimulation'], nextStatus);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    UPDATE_ACTIVE_BUTTON_CHART_SIMULATION: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'allActive'], action.payload.data);
        } else if (!children) {
          return newState.setIn([tabIndex, 'allActive'], action.payload.data);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },

    UPDATE_HIDE_BUTTON_CHART_SIMULATION: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'hideActive'], action.payload.data);
        } else if (!children) {
          return newState.setIn([tabIndex, 'hideActive'], action.payload.data);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_RECORDS_SET_CHART_SIMULATION: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingRecordSetsChart'], true);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingRecordSetsChart'], true);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_RECORDS_SET_SUMMARY_SIMULATION: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingRecordSetsSummary'], true);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingRecordSetsSummary'], true);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_RECORDS_SET_SUMMARY_SIMULATION_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingRecordSetsSummary'], false);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingRecordSetsSummary'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_RECORDS_SET_SUMMARY_SIMULATION_SUCCESS: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const summaryTable = Immutable.fromJS(getChartTable(Immutable.fromJS({ is: { details: action.payload.data } })));
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState
            .setIn([tabIndex, 'children', indexChild, 'fetchingRecordSetsSummary'], false)
            .setIn([tabIndex, 'children', indexChild, 'summaryTable'], Immutable.fromJS(summaryTable));
        } else if (!children) {
          return newState
            .setIn([tabIndex, 'fetchingRecordSetsSummary'], false)
            .setIn([tabIndex, 'summaryTable'], Immutable.fromJS(summaryTable));
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_RECORDS_SET_CHART_SIMULATION_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState
            .setIn([tabIndex, 'children', indexChild, 'fetchingRecordSetsChart'], false)
            .setIn([tabIndex, 'children', indexChild, 'recordsSets'], null);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingRecordSetsChart'], false).setIn([tabIndex, 'recordsSets'], null);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_RECORDS_SET_SIMULATION_SUCCESS: (state: DataStoreList, action: DataActionType) => {
      const recordSet = Object.assign({}, action.payload.data);
      const results = action.payload.data && action.payload.data.results.filter((metric: any) => metric.name !== 'is');
      recordSet.results = results;
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState
            .setIn([tabIndex, 'children', indexChild, 'fetchingRecordSets'], false)
            .setIn([tabIndex, 'children', indexChild, 'recordsSets'], Immutable.fromJS(recordSet));
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingRecordSets'], false).setIn([tabIndex, 'recordsSets'], Immutable.fromJS(recordSet));
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_RECORDS_SET_SIMULATION: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingRecordSets'], true);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingRecordSets'], true);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_RECORDS_SET_SIMULATION_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingRecordSets'], false);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingRecordSets'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    CHANGE_TYPE_OF_CHART: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState
            .setIn([tabIndex, 'children', indexChild, 'chartType'], action.payload.chartType)
            .setIn([tabIndex, 'children', indexChild, 'fetchingRecordSetsChart'], true);
        } else if (!children) {
          return newState.setIn([tabIndex, 'chartType'], action.payload.chartType).setIn([tabIndex, 'fetchingRecordSetsChart'], true);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_SUBMISSION_SIMULATION: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingSubmissionAlpha'], true);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingSubmissionAlpha'], true);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_SUBMISSION_SIMULATION_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingSubmissionAlpha'], false);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingSubmissionAlpha'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_SUBMISSION_SIMULATION_SUCCESS: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingSubmissionAlpha'], false);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingSubmissionAlpha'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_SIMULATION_CORRELATION: createRequestSimulationCorrelation('fetchingCorrelation', 'correlationSelf'),
    REQUEST_SIMULATION_CORRELATION_POWER_POOL: createRequestSimulationCorrelation('fetchingCorrelationPowerPool', 'correlationPowerPool'),
    RESPONSE_SIMULATION_CORRELATION_SUCCESS: createResponseSimulationCorrelationSuccess(
      'correlationSelf',
      'lastRunSelf',
      'fetchingCorrelation'
    ),
    RESPONSE_SIMULATION_CORRELATION_SUCCESS_POWER_POOL: createResponseSimulationCorrelationSuccess(
      'correlationPowerPool',
      'lastRunPowerPool',
      'fetchingCorrelationPowerPool'
    ),
    RESPONSE_SIMULATION_CORRELATION_FAILURE: createResponseSimulationCorrelationFailure('fetchingCorrelation'),
    RESPONSE_SIMULATION_CORRELATION_FAILURE_POWER_POOL: createResponseSimulationCorrelationFailure('fetchingCorrelationPowerPool'),
    REQUEST_SIMULATION_CORRELATION_PROD: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        const hasAlphaDetailChildren = newState.getIn([tabIndex, 'children', indexChild, 'alphaDetail']);
        const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0 && hasAlphaDetailChildren) {
          return newState
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'fetchingCorrelationProd'], true)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'correlationProd'], null);
        } else if (!children && hasAlphaDetail) {
          return newState
            .setIn([tabIndex, 'alphaDetail', 'fetchingCorrelationProd'], true)
            .setIn([tabIndex, 'alphaDetail', 'correlationProd'], null);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_SIMULATION_CORRELATION_PROD_SUCCESS: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        const hasAlphaDetailChildren = newState.getIn([tabIndex, 'children', indexChild, 'alphaDetail']);
        const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0 && hasAlphaDetailChildren) {
          return newState
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'fetchingCorrelationProd'], false)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'correlationProd'], Immutable.fromJS(action.payload.data))
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'lastRunProd'], moment().utc().format());
        } else if (!children && hasAlphaDetail) {
          return newState
            .setIn([tabIndex, 'alphaDetail', 'fetchingCorrelationProd'], false)
            .setIn([tabIndex, 'alphaDetail', 'correlationProd'], Immutable.fromJS(action.payload.data))
            .setIn([tabIndex, 'alphaDetail', 'lastRunProd'], moment().utc().format());
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_SIMULATION_CORRELATION_PROD_FAILURE: (state: DataStoreList, action: CorrelationFailureDataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        const simulationStatePath = [tabIndex, 'children', indexChild, 'alphaDetail'];
        const hasAlphaDetailChildren = newState.getIn(simulationStatePath);
        const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0 && hasAlphaDetailChildren) {
          return newState
            .setIn([...simulationStatePath, 'correlationStatusCode'], action.payload.statusCode)
            .setIn([...simulationStatePath, 'fetchingCorrelationProd'], false);
        } else if (!children && hasAlphaDetail) {
          const alphaStatePath = [tabIndex, 'alphaDetail'];
          return newState
            .setIn([...alphaStatePath, 'correlationStatusCode'], action.payload.statusCode)
            .setIn([...alphaStatePath, 'fetchingCorrelationProd'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_UPDATE_ALPHA_PROPERTIES_SIMULATION: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingAlphaPropertiesPatch'], true);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingAlphaPropertiesPatch'], true);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_SELECTION_CODE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      if (tabIndex >= 0) {
        return state
          .setIn([tabIndex, 'selectionCodeFetching'], true)
          .setIn([tabIndex, 'alphaDetail'], null)
          .setIn([tabIndex, 'alphasFromSelectionCode'], null)
          .setIn(
            [tabIndex, 'selectionCodeSettings'],
            Immutable.fromJS({
              selection: action.payload.selection,
              delay: action.payload.delay,
              region: action.payload.region,
              instrumentType: action.payload.instrumentType,
              selectionLimit: action.payload.selectionLimit,
              selectionHandling: action.payload.selectionHandling,
            })
          );
      } else {
        return state;
      }
    },
    RESPONSE_SELECTION_CODE_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      if (tabIndex >= 0) {
        return state
          .setIn([tabIndex, 'selectionCodeFetching'], false)
          .setIn([tabIndex, 'status'], ERROR_STATUS)
          .setIn([tabIndex, 'statusAfterSimulation'], ERROR_STATUS);
      } else {
        return state;
      }
    },
    RESPONSE_SELECTION_CODE_SUCCESS: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const data = action.payload.data;
      if (tabIndex >= 0) {
        return state.setIn([tabIndex, 'selectionCodeFetching'], false).setIn(
          [tabIndex, 'alphasFromSelectionCode'],
          Immutable.fromJS({
            count: data.count,
            results: data.results,
            message: data.message,
          })
        );
      } else {
        return state;
      }
    },
    RESPONSE_UPDATE_ALPHA_PROPERTIES_SIMULATION_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState.setIn([tabIndex, 'children', indexChild, 'fetchingAlphaPropertiesPatch'], false);
        } else if (!children) {
          return newState.setIn([tabIndex, 'fetchingAlphaPropertiesPatch'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_UPDATE_ALPHA_PROPERTIES_SIMULATION: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          return newState
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail'], Immutable.fromJS(action.payload.data))
            .setIn([tabIndex, 'children', indexChild, 'fetchingAlphaPropertiesPatch'], false);
        } else if (!children) {
          return newState
            .setIn([tabIndex, 'alphaDetail'], Immutable.fromJS(action.payload.data))
            .setIn([tabIndex, 'fetchingAlphaPropertiesPatch'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_SIMULATION_ALPHAS_PERFORMANCE_COMPARISON: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        const hasAlphaDetailChildren = newState.getIn([tabIndex, 'children', indexChild, 'alphaDetail']);
        const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0 && hasAlphaDetailChildren) {
          return newState
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceAlphaFetching'], true)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performancePnl', 'chartType'], null)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performancePnl', 'chartData'], null)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceTable'], null)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceStats'], null)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceAlphaLastDate'], null);
        } else if (!children && hasAlphaDetail) {
          return newState
            .setIn([tabIndex, 'alphaDetail', 'performanceAlphaFetching'], true)
            .setIn([tabIndex, 'alphaDetail', 'performancePnl', 'chartType'], null)
            .setIn([tabIndex, 'alphaDetail', 'performancePnl', 'chartData'], null)
            .setIn([tabIndex, 'alphaDetail', 'performanceTable'], null)
            .setIn([tabIndex, 'alphaDetail', 'performanceStats'], null)
            .setIn([tabIndex, 'alphaDetail', 'performanceAlphaLastDate'], null);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_SIMULATION_ALPHAS_PERFORMANCE_COMPARISON_SUCCESS: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      const performanceDataPnl = Object.assign({}, action.payload.data.pnl);
      const getRecordSeries = chartMapping['average-value-by-industry'];
      const { osStartDate, testStartDate, validationStartDate, trainStartDate } = getStartDates(state as unknown as AlphasStoreElement);
      const schema = action.payload.data.pnl.schema;
      const records = action.payload.data.pnl.records;
      const alphaSummary = Immutable.fromJS(updateAlphasSummary(action.payload.data.score, 'score'));
      const alphaDatafieldsScore = Immutable.fromJS(updateAlphasSummary(action.payload.data.datafieldsScore, 'datafieldsScore'));
      const alphaWeeklyScore = Immutable.fromJS(updateAlphasSummary(action.payload.data.weeklyScore, 'weeklyScore'));
      const series = getRecordSeries({
        chartDataSchema: schema,
        chartDataRecords: records,
        osStartDate,
        testStartDate,
        trainStartDate,
        validationStartDate,
        recordSet: 'average-value-by-industry',
      });
      const competition = action.payload.data.competition ? Immutable.fromJS(action.payload.data.competition) : null;
      const partition = Immutable.fromJS(action.payload.data.partition ?? []);
      const colors = ['#f69526', '#4fb6f5'];
      performanceDataPnl.records = series.map((serie: any, index: any) => {
        serie.color = colors[index];

        return serie;
      });
      const date = action.payload.date;
      const performanceTable = Immutable.fromJS(
        formatPerformanceData(action.payload.data.yearlyStats.before, action.payload.data.yearlyStats.after)
      );
      const performanceStats = Immutable.fromJS(formatPerformanceStats(action.payload.data.stats.before, action.payload.data.stats.after));
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        const hasAlphaDetailChildren = newState.getIn([tabIndex, 'children', indexChild, 'alphaDetail']);
        const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0 && hasAlphaDetailChildren) {
          return newState
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceAlphaFetching'], false)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performancePnl', 'chartType'], 'average-value-by-industry')
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceTable'], performanceTable)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceStats'], performanceStats)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performancePnl', 'chartData'], Immutable.fromJS(performanceDataPnl))
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceAlphaLastDate'], date)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceAlphaSummary'], alphaSummary)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceAlphaDatafieldsScore'], alphaDatafieldsScore)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceAlphaWeeklyScore'], alphaWeeklyScore)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performancePartition'], partition)
            .setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceCompetition'], competition);
        } else if (!children && hasAlphaDetail) {
          return newState
            .setIn([tabIndex, 'alphaDetail', 'performanceAlphaFetching'], false)
            .setIn([tabIndex, 'alphaDetail', 'performanceTable'], performanceTable)
            .setIn([tabIndex, 'alphaDetail', 'performanceStats'], performanceStats)
            .setIn([tabIndex, 'alphaDetail', 'performancePnl', 'chartType'], 'average-value-by-industry')
            .setIn([tabIndex, 'alphaDetail', 'performancePnl', 'chartData'], Immutable.fromJS(performanceDataPnl))
            .setIn([tabIndex, 'alphaDetail', 'performanceAlphaLastDate'], date)
            .setIn([tabIndex, 'alphaDetail', 'performanceAlphaSummary'], alphaSummary)
            .setIn([tabIndex, 'alphaDetail', 'performanceAlphaDatafieldsScore'], alphaDatafieldsScore)
            .setIn([tabIndex, 'alphaDetail', 'performanceAlphaWeeklyScore'], alphaWeeklyScore)
            .setIn([tabIndex, 'alphaDetail', 'performancePartition'], partition)
            .setIn([tabIndex, 'alphaDetail', 'performanceCompetition'], competition);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    RESPONSE_SIMULATION_ALPHAS_PERFORMANCE_COMPARISON_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        const hasAlphaDetailChildren = newState.getIn([tabIndex, 'children', indexChild, 'alphaDetail']);
        const hasAlphaDetail = newState.getIn([tabIndex, 'alphaDetail']);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0 && hasAlphaDetailChildren) {
          return newState.setIn([tabIndex, 'children', indexChild, 'alphaDetail', 'performanceAlphaFetching'], false);
        } else if (!children && hasAlphaDetail) {
          return newState.setIn([tabIndex, 'alphaDetail', 'performanceAlphaFetching'], false);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    SAVE_SIMULATION_PROPERTIES: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      const newState = state;
      if (tabIndex >= 0) {
        const childId = action.payload.childId;
        const children = state.getIn([tabIndex, 'children']);
        // @ts-ignore
        const indexChild = children && childId && children.findIndex((item: any) => item.get('id') === childId);
        if (indexChild !== null && indexChild !== undefined && indexChild >= 0) {
          // @ts-ignore
          const alphaProperties = newState.getIn([tabIndex, 'children', indexChild, 'alphaProperties']).merge(
            Immutable.fromJS({
              ...action.payload.data,
              hasUpdatedProperties: true,
              dontOverrideProperties: true,
            })
          );

          return newState.setIn([tabIndex, 'children', indexChild, 'alphaProperties'], alphaProperties);
        } else if (!children) {
          // @ts-ignore
          const alphaProperties = newState.getIn([tabIndex, 'alphaProperties']).merge(
            Immutable.fromJS({
              ...action.payload.data,
              hasUpdatedProperties: true,
              dontOverrideProperties: true,
            })
          );

          return newState.setIn([tabIndex, 'alphaProperties'], alphaProperties);
        } else {
          return state;
        }
      } else {
        return state;
      }
    },
    REQUEST_ALPHAS_FROM_ALPHA_SIMULATE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      if (tabIndex >= 0) {
        return state.setIn([tabIndex, 'fetchingAlphaSelections'], true);
      } else {
        return state;
      }
    },
    CHANGE_TAB_POSITION: (state: DataStoreList, action: DataActionType) => {
      const tabToChange = state.get(action.payload.source);
      if (tabToChange) {
        return state.delete(action.payload.source).insert(action.payload.destination, tabToChange);
      } else {
        return state;
      }
    },
    CHANGE_TAB_POSITION_CHILD: (state: DataStoreList, action: DataActionType) => {
      const tabToChange = state.getIn([action.payload.currentActiveTab, 'children', action.payload.source]);
      const childrenTabs = state.getIn([action.payload.currentActiveTab, 'children']);
      if (tabToChange) {
        // @ts-ignore
        const newChildrenTabs = childrenTabs.delete(action.payload.source).insert(action.payload.destination, tabToChange);

        return state.setIn([action.payload.currentActiveTab, 'children'], newChildrenTabs);
      } else {
        return state;
      }
    },
    RESPONSE_ALPHAS_FROM_ALPHA_SIMULATE_FAILURE: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      if (tabIndex >= 0) {
        return state.setIn([tabIndex, 'fetchingAlphaSelections'], false);
      } else {
        return state;
      }
    },
    RESPONSE_ALPHAS_FROM_ALPHA_SIMULATE_SUCCESS: (state: DataStoreList, action: DataActionType) => {
      const tabIndex = state.findIndex((item: any) => item.get('id') === action.payload.idParent);
      if (tabIndex >= 0) {
        return state
          .setIn([tabIndex, 'fetchingAlphaSelections'], false)
          .setIn([tabIndex, 'alphaSelections'], Immutable.fromJS(action.payload.alpha));
      } else {
        return state;
      }
    },
    CHANGE_SIMULATION_STATUS: (state: DataStoreList, action: DataActionType) => {
      let tabIndex: any = action.payload.tabIndex;
      const indexChild: any = action.payload.indexChild;
      if (typeof tabIndex === 'string' || tabIndex instanceof String) {
        state.forEach((data: any, index: number) => {
          if (data?.get('id') === tabIndex) {
            tabIndex = index;
          }
          if (data === undefined) {
            Sentry.withScope(scope => {
              scope.setExtras({
                state: JSON.stringify(state.toJS()),
                action,
              });
              Sentry.captureException(new Error('CHANGE_SIMULATION_STATUS error'));
            });
          }
        });
      }
      // Set new given tab state
      if (action.payload.nextStatus) {
        return setNewStatus(state, tabIndex, indexChild, action.payload.nextStatus);
      }
      // Set new tab state, following the normal state flow:
      //    NEW state       > (SIMULATING state) UNREAD state, ERROR state, WARNING state
      //    UNREAD state    > COMPLETED state
      //    COMPLETED state > EDITED state
      //    EDITED state    > (SIMULATING state) UNREAD state, ERROR state, WARNING state
      //    ERROR state     > NEW state
      //    WARNING state   > EDITED state
      switch (action.payload.currentStatus) {
        case UNREAD_STATUS:
          return setNewStatus(state, tabIndex, indexChild, COMPLETED_STATUS);
        case COMPLETED_STATUS:
        case WARNING_STATUS:
          return setNewStatus(state, tabIndex, indexChild, EDITED_STATUS);
        case ERROR_STATUS:
          return setNewStatus(state, tabIndex, indexChild, NEW_STATUS);
        default:
          return state;
      }
    },
    WALKTHROUGH_WITH_CHART_PROGRESS: (state: DataStoreList) => {
      const currentIndex = state.size - 1;
      let newData: any = state.get(currentIndex);

      if (newData) {
        newData = newData.mergeDeep(Immutable.fromJS(walkthroughWithData));

        return state.set(currentIndex, newData);
      } else {
        return state;
      }
    },
    WALKTHROUGH_WITHOUT_CHART_PROGRESS: (state: DataStoreList) => {
      const currentIndex = state.size - 1;
      let newData: any = state.get(currentIndex);

      if (newData) {
        newData = newData.mergeDeep(Immutable.fromJS(walkthroughWithoutData));

        return state.set(currentIndex, newData);
      } else {
        return state;
      }
    },
    AUTHENTICATION_LOGOUT: () => initialState,
    SET_ALPHA_TEST_PERIOD_SHOWN: (state: AlphasStoreElement, action: AlphasActionType) => {
      if (action.payload.alphaDetailPath?.[0] === 'simulation') {
        return state.setIn(
          [...action.payload.alphaDetailPath!.slice(2), 'isTestPeriodShown'],
          Immutable.fromJS(action.payload.isTestPeriodShown)
        );
      }
      return state;
    },
  },
  initialState
);
